import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button, Card, Col, Input, Row, Space, Table, Typography } from 'antd';
import { notification } from 'antd';
import axios from 'axios';
import dayjs from 'dayjs';

import { programLabels } from '@/config';
import { getManufacturerByProgram } from '@/libs/util';
import useGlobalConfig from '@/store/global';

const { Title } = Typography;

const HomePage = () => {
  const [tableLoading, setTableLoading] = useState(true);
  const { themeColor, program } = useGlobalConfig();
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState<string | undefined>();
  const [lastName, setLastName] = useState<string | undefined>();
  const [dob, setDob] = useState<string | undefined>();
  const [data, setData] = useState<any[]>([]);
  const [api, contextHolder] = notification.useNotification();
  const [searchLoading, setSearchLoading] = useState(false);
  const isOctaPharma = getManufacturerByProgram(program.name)?.indexOf('Octapharma') !== -1;
  const commonTableItems = [
    {
      title: 'First Name',
      dataIndex: 'patient_first_name',
      key: 'patient_first_name',
    },
    {
      title: 'Last Name',
      dataIndex: 'patient_last_name',
      key: 'patient_last_name',
    },
    {
      title: 'Date of Birth',
      dataIndex: 'patient_dob',
      key: 'patient_dob',
      render: (text: string) => dayjs(text).format('MM-DD-YYYY'),
    },
    { title: 'Phone Number', dataIndex: 'patient_phone', key: 'patient_phone' },
    {
      title: 'Gender',
      dataIndex: 'patient_gender',
      key: 'patient_gender',
      render: (text: string) => (text === '1' ? 'Male' : 'Female'),
    },
    { title: 'State', dataIndex: 'patient_state', key: 'patient_state' },
    {
      title: 'Status',
      dataIndex: 'benefit_investigation_result_status',
      key: 'benefit_investigation_result_status',
    },
  ];

  const octapharmaTableItems: any[] = [];

  const handleSearch = async () => {
    try {
      setSearchLoading(true);
      const params = new URLSearchParams();
      if (firstName) params.append('first_name', firstName);
      if (lastName) params.append('last_name', lastName);
      if (dob) params.append('date_of_birth', dob);

      const url = `benefit_investigation_requests?${params.toString()}`;
      const { data } = await axios.get(url);
      setData(data);
    } catch (e) {
      api.error({
        message: 'Search failed',
        description: 'Something is wrong.',
        duration: 0,
      });
      console.error(e);
    } finally {
      setSearchLoading(false);
    }
  };

  const loadData = async () => {
    try {
      const { data } = await axios.get('benefit_investigation_requests');
      setData(data);
    } catch (e) {
      api.error({
        message: 'Load data failed',
        description: 'Something is wrong.',
        duration: 0,
      });
      console.error(e);
    } finally {
      setTableLoading(false);
    }
  };

  let renderRef = useRef(false);

  useEffect(() => {
    if (renderRef.current) {
      renderRef.current = false;
      return;
    }
    loadData();
  }, []);

  const HandleButtons = (
    <Row gutter={16} className="mb-6 mt-3">
      <Col>
        <Button
          type="primary"
          style={{ backgroundColor: themeColor, borderColor: themeColor }}
          onClick={() => navigate('/requests/special-requests/new')}
        >
          Request Benefits Investigation/Prior Authorization
        </Button>
      </Col>
    </Row>
  );

  return (
    <>
      {contextHolder}
      {!isOctaPharma && (
        <Card>
          <Title level={2}>{programLabels[program.name].title}</Title>

          <span className="rounded-md bg-green-200 p-2 text-green-700">
            Signed in as {localStorage.getItem('point_of_contact')} at{' '}
            {localStorage.getItem('business_name')}
          </span>

          {HandleButtons}
        </Card>
      )}

      {isOctaPharma && <>{HandleButtons}</>}

      <Row gutter={24}>
        <Col span={24}>
          <Title level={4}>Search patient</Title>
        </Col>
        <Col span={6}>
          <Input
            placeholder="First name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
          />
        </Col>
        <Col span={6}>
          <Input
            placeholder="Last name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
          />
        </Col>
        <Col span={6}>
          <Input
            placeholder="DOB (yyyy-mm-dd)"
            value={dob}
            onChange={(e) => setDob(e.target.value)}
          />
        </Col>
        <Col span={6}>
          <Button
            loading={searchLoading}
            type="primary"
            disabled={searchLoading}
            style={{ backgroundColor: themeColor, borderColor: themeColor }}
            onClick={handleSearch}
          >
            Search
          </Button>
        </Col>
      </Row>
      <Table
        loading={tableLoading}
        rowKey="id"
        bordered
        className="my-6"
        dataSource={data} // This should be replaced with your data source
        columns={[
          ...(isOctaPharma
            ? [
                ...commonTableItems.slice(0, 4),
                ...octapharmaTableItems,
                ...commonTableItems.slice(4),
              ]
            : commonTableItems),
          {
            title: 'Action',
            key: 'action',
            align: 'center',
            width: '100px',
            render: (_, record) => (
              <Space size="middle">
                <Button onClick={() => navigate(`/requests/bi-request/${record.id}`)}>
                  {record?.benefit_investigation_result_status === 'BI Completed'
                    ? 'View Result'
                    : 'View Request'}
                </Button>
              </Space>
            ),
          },
        ]}
      />
    </>
  );
};

export default HomePage;
